<template>
  <section>
    <div class="hidden md:block mx-auto xl:px-0 xl:max-w-6xl py-12">
      <div class="flex justify-center text-center pb-14">
        <h2 class="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-medium text-gray-100">
          Suite de herramientas intuitivas
          <br />
          para automatizar procesos.
        </h2>
      </div>

      <div class="md:flex lg:gap-6 lg:space-y-0 items-center">
        <div class="md:w-1/2 mt-[-45px]">
          <div class="text-center md:text-left">
            <div class="text-left pb-6 pl-6">
              <h2 class="text-base text-gray-100 pt-3">
                Optimice y automatice procesos con nuestra suite intuitiva, liberando tiempo para
                estrategias clave. Su interfaz amigable mejora la eficiencia y productividad.
              </h2>
            </div>

            <div
              class="mx-auto bg-gradient-to-r from-gray-900 via-white to-gray-900 text-center text-white mb-8"
              style="height: 1px"
            ></div>

            <div
              class="flex items-start mb-0 p-5"
              :class="{
                'border rounded-xl !items-start bg-white shadow-md':
                  currentVideoUrl === videoPaths[0]
              }"
            >
              <div>
                <svg class="mr-3" width="30" height="30">
                  <circle
                    v-if="currentVideoUrl === videoPaths[0]"
                    cx="16"
                    cy="16"
                    r="13"
                    stroke="#7947A1"
                    fill="none"
                    stroke-width="2"
                    :style="{ strokeDasharray: dashArray + ' 100', strokeDashoffset: dashOffset }"
                  />
                </svg>
                <svg
                  class="h-5 w-5 circle-center"
                  fill="white"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  :class="{ 'fill-onClick': currentVideoUrl === videoPaths[0] }"
                >
                  <path
                    d="M22 7V16C22 17.1 21.1 18 20 18H6L2 22V4C2 2.9 2.9 2 4 2H14.1C14 2.3 14 2.7 14 3S14 3.7 14.1 4H4V16H20V7.9C20.7 7.8 21.4 7.4 22 7M16 3C16 4.7 17.3 6 19 6S22 4.7 22 3 20.7 0 19 0 16 1.3 16 3Z"
                  />
                </svg>
              </div>
              <div class="flex items-start flex-col">
                <button
                  @click="changeVideo(videoPaths[0])"
                  class="text-base font-medium sm:text-lg text-gray-100"
                  :class="{ '!font-bold !text-black': currentVideoUrl === videoPaths[0] }"
                >
                  Gestión en tiempo real de la IA
                </button>
                <p v-if="currentVideoUrl === videoPaths[0]" class="text-base text-gray-800 mt-2">
                  Automatice la asignación de clientes potenciales y mejore la atención al cliente
                  con nuestra solución de IA para conversaciones en tiempo real.
                </p>
              </div>
            </div>
            <div
              class="flex items-center mb-0 p-5"
              :class="{
                'border rounded-xl !items-start bg-white shadow-md':
                  currentVideoUrl === videoPaths[1]
              }"
            >
              <div>
                <svg class="mr-3" width="30" height="30">
                  <circle
                    v-if="currentVideoUrl === videoPaths[1]"
                    cx="16"
                    cy="16"
                    r="13"
                    stroke="#7947A1"
                    fill="none"
                    stroke-width="2"
                    :style="{ strokeDasharray: dashArray + ' 100', strokeDashoffset: dashOffset }"
                  />
                </svg>
                <svg
                  class="h-6 w-6 -mt-7 ml-1"
                  fill="white"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  :class="{ 'fill-onClick': currentVideoUrl === videoPaths[1] }"
                >
                  <path
                    d="M12,8H4A2,2 0 0,0 2,10V14A2,2 0 0,0 4,16H5V20A1,1 0 0,0 6,21H8A1,1 0 0,0 9,20V16H12L17,20V4L12,8M15,15.6L13,14H4V10H13L15,8.4V15.6M21.5,12C21.5,13.71 20.54,15.26 19,16V8C20.53,8.75 21.5,10.3 21.5,12Z"
                  />
                </svg>
              </div>
              <div class="flex items-start flex-col">
                <button
                  @click="changeVideo(videoPaths[1])"
                  class="text-base font-medium sm:text-lg text-gray-100"
                  :class="{ '!font-bold !text-black': currentVideoUrl === videoPaths[1] }"
                >
                  Campañas y boletines
                </button>
                <p v-if="currentVideoUrl === videoPaths[1]" class="text-base text-gray-800 mt-2">
                  Optimice sus estrategias de marketing con nuestras campañas y boletines, diseñados
                  para maximizar el alcance y la efectividad en la interacción con sus clientes.
                </p>
              </div>
            </div>
            <div
              class="flex items-center mb-0 p-5"
              :class="{
                'border rounded-xl !items-start bg-white shadow-md':
                  currentVideoUrl === videoPaths[2]
              }"
            >
              <div>
                <svg class="mr-3" width="30" height="30" style="display: block">
                  <circle
                    v-if="currentVideoUrl === videoPaths[2]"
                    cx="16"
                    cy="16"
                    r="13"
                    stroke="#7947A1"
                    fill="none"
                    stroke-width="2"
                    :style="{ strokeDasharray: dashArray + ' 100', strokeDashoffset: dashOffset }"
                  />
                </svg>
                <svg
                  class="h-6 w-6 circle-center-custom"
                  fill="white"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  :class="{ 'fill-onClick': currentVideoUrl === videoPaths[2] }"
                >
                  <path
                    d="M21.5 9H16.5L18.36 7.14C16.9 5.23 14.59 4 12 4C7.58 4 4 7.58 4 12C4 13.83 4.61 15.5 5.64 16.85C6.86 15.45 9.15 14.5 12 14.5C14.85 14.5 17.15 15.45 18.36 16.85C19.39 15.5 20 13.83 20 12H22C22 17.5 17.5 22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C15.14 2 17.95 3.45 19.78 5.72L21.5 4V9M12 20C13.9 20 15.64 19.34 17 18.24C16.36 17.23 14.45 16.5 12 16.5C9.55 16.5 7.64 17.23 7 18.24C8.36 19.34 10.1 20 12 20M12 6C13.93 6 15.5 7.57 15.5 9.5C15.5 11.43 13.93 13 12 13C10.07 13 8.5 11.43 8.5 9.5C8.5 7.57 10.07 6 12 6M12 8C11.17 8 10.5 8.67 10.5 9.5C10.5 10.33 11.17 11 12 11C12.83 11 13.5 10.33 13.5 9.5C13.5 8.67 12.83 8 12 8Z"
                  />
                </svg>
              </div>
              <div class="flex items-start flex-col">
                <button
                  @click="changeVideo(videoPaths[2])"
                  class="text-base font-medium sm:text-lg text-gray-100"
                  :class="{ '!font-bold !text-black': currentVideoUrl === videoPaths[2] }"
                >
                  Acceso a soporte 24/7
                </button>
                <p v-if="currentVideoUrl === videoPaths[2]" class="text-base text-gray-800 mt-2">
                  Garantice tranquilidad con nuestro acceso ininterrumpido a soporte técnico, las 24
                  horas del día, los 7 días de la semana, atendemos sus necesidades en cualquier
                  momento.
                </p>
              </div>
            </div>
            <div
              class="flex items-center mb-0 p-5"
              :class="{
                'border rounded-xl !items-start bg-white shadow-md':
                  currentVideoUrl === videoPaths[3]
              }"
            >
              <div>
                <svg class="mr-3" width="30" height="30" style="display: block">
                  <circle
                    v-if="currentVideoUrl === videoPaths[3]"
                    cx="16"
                    cy="16"
                    r="13"
                    stroke="#7947A1"
                    fill="none"
                    stroke-width="2"
                    :style="{ strokeDasharray: dashArray + ' 100', strokeDashoffset: dashOffset }"
                  />
                </svg>
                <svg
                  class="h-6 w-6 circle-center-custom"
                  fill="white"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  :class="{ 'fill-onClick': currentVideoUrl === videoPaths[3] }"
                >
                  <path d="M6,13H18V11H6M3,6V8H21V6M10,18H14V16H10V18Z" />
                </svg>
              </div>
              <div class="flex items-start flex-col">
                <button
                  @click="changeVideo(videoPaths[3])"
                  class="text-base font-medium sm:text-lg text-gray-100"
                  :class="{ '!font-bold !text-black': currentVideoUrl === videoPaths[3] }"
                >
                  Embudos de ventas
                </button>
                <p v-if="currentVideoUrl === videoPaths[3]" class="text-base text-gray-800 mt-2">
                  Desarrolle embudos de ventas efectivos para guiar a sus clientes desde el primer
                  contacto hasta la conversión, maximizando así el rendimiento de su estrategia
                  comercial.
                </p>
              </div>
            </div>
            <div
              class="flex items-center mb-0 p-5"
              :class="{
                'border rounded-xl !items-start bg-white shadow-md':
                  currentVideoUrl === videoPaths[4]
              }"
            >
              <div>
                <svg class="mr-3" width="30" height="30" style="display: block">
                  <circle
                    v-if="currentVideoUrl === videoPaths[4]"
                    cx="16"
                    cy="16"
                    r="13"
                    stroke="#7947A1"
                    fill="none"
                    stroke-width="2"
                    :style="{ strokeDasharray: dashArray + ' 100', strokeDashoffset: dashOffset }"
                  />
                </svg>
                <svg
                  class="h-5 w-5 circle-center"
                  fill="gray"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  :class="{ 'fill-onClick': currentVideoUrl === videoPaths[4] }"
                >
                  <path
                    d="M22 5V7H19V10H17V7H14V5H17V2H19V5H22M19 19H5V5H11V3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V13H19V19M15 13V17H17V13H15M11 17H13V9H11V17M9 17V11H7V17H9Z"
                  />
                </svg>
              </div>
              <div class="flex items-start flex-col">
                <button
                  @click="changeVideo(videoPaths[4])"
                  class="text-base font-medium sm:text-lg text-gray-100"
                  :class="{ '!font-bold !text-black': currentVideoUrl === videoPaths[4] }"
                >
                  Analítica
                </button>
                <p v-if="currentVideoUrl === videoPaths[4]" class="text-base text-gray-800 mt-2">
                  Obtenga información valiosa y tome decisiones fundamentadas con nuestra sección de
                  analítica, que le proporciona un profundo entendimiento de sus datos y métricas
                  clave.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="md:w-1/2 pl-4 iphone-frame flex justify-center h-full" style="height: 600px">
          <div v-for="(videoUrl, index) in videoPaths" :key="index">
            <video
              class="video-player"
              autoplay
              loop
              muted
              v-if="currentVideoUrl === videoUrl"
              :src="videoUrl"
            ></video>
          </div>
        </div>
      </div>
    </div>

    <div class="md:hidden py-10 md:py-16 lg:py-36">
      <div class="text-center md:text-left">
        <h2 class="text-2xl font-medium text-gray-100 text-center">
          Suite de herramientas intuitivas para automatizar procesos.
        </h2>
        <p class="mt-8 text-gray-100 text-center md:text-start text-x px-4">
          Optimice y automatice procesos con nuestra suite intuitiva, liberando tiempo para
          estrategias clave. Su interfaz amigable mejora la eficiencia y productividad.
        </p>
      </div>
      <div class="mt-10 iphone-frame flex justify-center h-full" style="height: 600px">
        <div v-for="(videoUrl, index) in videoPaths" :key="index">
          <video
            class="video-player"
            autoplay
            loop
            muted
            v-if="currentVideoUrl === videoUrl"
            :src="videoUrl"
            preload="metadata"
            style="left: 0"
          ></video>
        </div>
      </div>
      <div
        class="flex overflow-x-auto snap-x snap-mandatory -mx-4 p-4 carousel"
        ref="carousel"
        style="scrollbar-width: none"
      >
        <div
          class="snap-center shrink-0 first:pl-4 last:pr-4 mr-4 ml-2 flex justify-center"
          v-for="(card, index) in cardData"
          :key="index"
        >
          <div class="w-full inline-block pb-0 p-2 border rounded-xl bg-white shadow-md max-w-xs">
            <svg class="mr-3" width="30" height="30">
              <circle
                v-if="currentVideoUrl === card.videoPath"
                cx="16"
                cy="16"
                r="13"
                stroke="#7947A1"
                fill="none"
                stroke-width="2"
                style="margin-top: 50px; position: absolute"
                :style="{ strokeDasharray: dashArray + ' 100', strokeDashoffset: dashOffset }"
              />
            </svg>
            <div class="flex items-start">
              <div class="mr-3" v-html="card.svg"></div>
              <div class="flex items-start flex-col">
                <button
                  @click="changeVideo(videoPaths[index])"
                  class="-mt-6 text-base font-medium sm:text-lg text-gray-500 !font-bold !text-black"
                >
                  {{ card.title }}
                </button>
                <p class="text-base text-gray-700 mt-2">
                  {{ card.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';

const videoPaths = [
  '/videos/gestion.mp4',
  '/videos/campanas.mp4',
  '/videos/soporte.mp4',
  '/videos/ventas.mp4',
  '/videos/analista.mp4'
];

const carousel = ref(null);
const currentVideoUrl = ref(videoPaths[0]);
const dashArray = ref(82.83);
const dashOffset = ref(82.83);
let intervalId = null;

const cardData = ref([
  {
    title: 'Gestión en tiempo real de la IA',
    svg:
      '<svg\n' +
      '                    class="h-5 w-5 -mt-[1.5rem] ml-[0.4rem]" fill="#7947A1" xmlns="http://www.w3.org/2000/svg"\n' +
      '                    viewBox="0 0 24 24">' +
      '                  <path d="M22 7V16C22 17.1 21.1 18 20 18H6L2 22V4C2 2.9 2.9 2 4 2H14.1C14 2.3 14 2.7 14 3S14 3.7 14.1 4H4V16H20V7.9C20.7 7.8 21.4 7.4 22 7M16 3C16 4.7 17.3 6 19 6S22 4.7 22 3 20.7 0 19 0 16 1.3 16 3Z"/>\n' +
      '                </svg>',
    description:
      'Automatice la asignación de clientes potenciales y mejore la atención al cliente con nuestra solución de IA para conversaciones en tiempo real.',
    videoPath: videoPaths[0]
  },
  {
    title: 'Campañas y boletines',
    svg:
      '<svg\n' +
      '                    class="h-6 w-6 -mt-7 ml-1" fill="#7947A1" xmlns="http://www.w3.org/2000/svg"\n' +
      '                    viewBox="0 0 24 24">' +
      '                  <path d="M12,8H4A2,2 0 0,0 2,10V14A2,2 0 0,0 4,16H5V20A1,1 0 0,0 6,21H8A1,1 0 0,0 9,20V16H12L17,20V4L12,8M15,15.6L13,14H4V10H13L15,8.4V15.6M21.5,12C21.5,13.71 20.54,15.26 19,16V8C20.53,8.75 21.5,10.3 21.5,12Z"/>\n' +
      '                </svg>',
    description:
      'Optimice sus estrategias de marketing con nuestras campañas y boletines, diseñados para maximizar el alcance y la efectividad.',
    videoPath: videoPaths[1]
  },
  {
    title: 'Acceso a soporte 24/7',
    svg:
      '<svg class="h-6 w-6 -mt-[1.63rem] ml-[0.25rem]" fill="#7947A1"\n' +
      '                     xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">' +
      '                  <path d="M21.5 9H16.5L18.36 7.14C16.9 5.23 14.59 4 12 4C7.58 4 4 7.58 4 12C4 13.83 4.61 15.5 5.64 16.85C6.86 15.45 9.15 14.5 12 14.5C14.85 14.5 17.15 15.45 18.36 16.85C19.39 15.5 20 13.83 20 12H22C22 17.5 17.5 22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C15.14 2 17.95 3.45 19.78 5.72L21.5 4V9M12 20C13.9 20 15.64 19.34 17 18.24C16.36 17.23 14.45 16.5 12 16.5C9.55 16.5 7.64 17.23 7 18.24C8.36 19.34 10.1 20 12 20M12 6C13.93 6 15.5 7.57 15.5 9.5C15.5 11.43 13.93 13 12 13C10.07 13 8.5 11.43 8.5 9.5C8.5 7.57 10.07 6 12 6M12 8C11.17 8 10.5 8.67 10.5 9.5C10.5 10.33 11.17 11 12 11C12.83 11 13.5 10.33 13.5 9.5C13.5 8.67 12.83 8 12 8Z"/>\n' +
      '                </svg>',
    description:
      'Garantice tranquilidad con nuestro acceso ininterrumpido a soporte técnico, las\n' +
      '                  24 horas\n' +
      '                  del día, los 7 días de la semana, atendemos sus necesidades en cualquier\n' +
      '                  momento.',
    videoPath: videoPaths[2]
  },
  {
    title: 'Embudos de ventas',
    svg:
      '<svg class="h-6 w-6 -mt-[1.63rem] ml-[0.25rem]" fill="#7947A1" xmlns="http://www.w3.org/2000/svg" ' +
      'viewBox="0 0 24 24">' +
      '<path d="M6,13H18V11H6M3,6V8H21V6M10,18H14V16H10V18Z"/></svg>',
    description:
      'Desarrolle embudos de ventas efectivos para guiar a sus clientes desde el primer\n' +
      '                  contacto hasta la conversión, maximizando así el rendimiento de su estrategia\n' +
      '                  comercial.',
    videoPath: videoPaths[3]
  },
  {
    title: 'Analítica',
    svg:
      '<svg class="h-5 w-5 -mt-[1.5rem] ml-[0.4rem]" fill="#7947A1" xmlns="http://www.w3.org/2000/svg" ' +
      'viewBox="0 0 24 24">' +
      '<path d="M22 5V7H19V10H17V7H14V5H17V2H19V5H22M19 19H5V5H11V3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V13H19V19M15 13V17H17V13H15M11 17H13V9H11V17M9 17V11H7V17H9Z"/></svg>',
    description:
      'Obtenga información valiosa y tome decisiones fundamentadas con nuestra sección\n' +
      '                  de\n' +
      '                  analítica, que le proporciona un profundo entendimiento de sus datos y métricas\n' +
      '                  clave.',
    videoPath: videoPaths[4]
  }
]);

const changeVideo = (videoPath) => {
  if (currentVideoUrl.value === videoPath) {
    return;
  }
  currentVideoUrl.value = videoPath;
  resetAnimation();
};

const resetAnimation = () => {
  dashOffset.value = dashArray.value;
};

watch(currentVideoUrl, (newUrl) => {
  dashOffset.value = dashArray.value;
  nextTick(() => {
    const videoElements: NodeListOf<HTMLVideoElement> = document.querySelectorAll('.video-player');

    videoElements.forEach((video) => {
      if (video.getAttribute('src') === newUrl) {
        video.pause();
        video.currentTime = 0;
        video.play();
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  });
  updateCarouselFocus();
});

const startVideoRotation = () => {
  intervalId = setInterval(() => {
    dashOffset.value -= 82.83 / 230;
    if (dashOffset.value <= 0) {
      let currentIndex = videoPaths.indexOf(currentVideoUrl.value);
      currentIndex = (currentIndex + 1) % videoPaths.length;
      changeVideo(videoPaths[currentIndex]);
    }
  }, 100);
};

const stopVideoRotation = () => {
  clearInterval(intervalId);
};

function debounce(fn: Function, delay: number) {
  let timeoutID = null;

  return function () {
    clearTimeout(timeoutID);
    timeoutID = setTimeout(() => {
      fn.apply(null, arguments);
    }, delay);
  };
}

const handleScroll = debounce(() => {
  const scrollPosition = carousel.value.scrollLeft + carousel.value.offsetWidth / 2;
  const cards = carousel.value.children;
  let centeredIndex = 0;

  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    const cardStart = card.offsetLeft;
    const cardEnd = cardStart + card.offsetWidth;

    if (scrollPosition >= cardStart && scrollPosition <= cardEnd) {
      centeredIndex = i;
      break;
    }
  }

  currentVideoUrl.value = videoPaths[centeredIndex];
}, 100);

const updateCarouselFocus = () => {
  nextTick(() => {
    const activeCardIndex = videoPaths.indexOf(currentVideoUrl.value);
    const carouselElement = carousel.value;
    const cards = carouselElement.children;
    const activeCard = cards[activeCardIndex];

    if (activeCard) {
      const scrollTarget =
        activeCard.offsetLeft + activeCard.offsetWidth / 2 - carouselElement.offsetWidth / 2;
      carouselElement.scrollTo({ left: scrollTarget, behavior: 'smooth' });
    }
  });
};

onMounted(() => {
  startVideoRotation();
  updateCarouselFocus();

  if (carousel.value) {
    carousel.value.addEventListener('scroll', handleScroll);
  }
});

onUnmounted(() => {
  if (carousel.value) {
    carousel.value.removeEventListener('scroll', handleScroll);
  }
  stopVideoRotation();
});
</script>

<style scoped>
.circle-center {
  margin-top: -1.5rem;
  margin-left: 0.4rem;
}

.circle-center-custom {
  margin-top: -1.63rem;
  margin-left: 0.25rem;
}

.fill-onClick {
  fill: #7947a1;
}

.iphone-frame {
  position: relative;
  background: url('/images/iphone3.png') center/contain no-repeat;
}

.video-player {
  top: 13px;
  left: -8px;
  position: relative;
  width: 268px;
  height: 574px;
  border-radius: 34px !important;
  object-fit: fill;
}
</style>
